/**
 * Property (Business Unit) Detail Page — /clients/:clientId/properties/:buId
 *
 * Consolidated view of a single property with every operational surface
 * that touches it:
 *   - Identity card (name, URL, priority, OG image, brief summary)
 *   - Budget vs actual + traffic delta (reuses /budget-summary)
 *   - Domains attached (from /clients/:id/domains, filtered by bu_id)
 *   - **WordPress sites attached** — the immediate driver of this
 *     page. Uses /business-units/:bu_id/wp-sites which does an
 *     explicit-then-hostname-fallback match. Each site row:
 *       • Beacon status pill (fresh / stale / offline / not installed)
 *       • Direct link to the WordPress API page filtered to this site
 *       • Direct link to the per-site Beacon Panel
 *       • Detach button (admin only) — clears business_unit_id via PATCH
 *   - Attach-a-WP-site picker showing unlinked sites in the client
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Loader2, Globe2, ExternalLink, PanelsTopLeft,
  Signal, SignalHigh, SignalMedium, SignalLow, Plus, Unlink,
} from "lucide-react";
import { toast } from "sonner";
import { PlatformPills } from "@/components/PropertyPlatformsEditor";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Pydantic v2 returns `detail` as an ARRAY of validation errors, not a
// string. Passing that array straight into toast.error() causes Sonner
// to try to render it as a React child which explodes ("Objects are
// not valid as a React child"). Coerce anything non-string into a
// short human-readable label.
function errText(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d.length > 0) {
    const first = d[0];
    return first?.msg || fallback;
  }
  return err?.message || fallback;
}

function beaconFreshnessBadge(site) {
  if (!site.beacon_created_at && !site.detected?.beacon) {
    return (
      <Badge variant="outline"
        className="rounded-sm text-[10px] bg-zinc-100 text-zinc-500 border-zinc-200">
        <Signal className="h-3 w-3 mr-1" /> no beacon
      </Badge>
    );
  }
  const lastSeen = site.beacon_last_seen_at
    ? new Date(site.beacon_last_seen_at).getTime() : null;
  if (!lastSeen) {
    return (
      <Badge variant="outline"
        className="rounded-sm text-[10px] bg-zinc-100 text-zinc-500 border-zinc-200">
        <Signal className="h-3 w-3 mr-1" /> configured
      </Badge>
    );
  }
  const mins = Math.round((Date.now() - lastSeen) / 60000);
  if (mins < 30) {
    return (
      <Badge variant="outline"
        className="rounded-sm text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
        <SignalHigh className="h-3 w-3 mr-1" /> fresh · {mins}m ago
      </Badge>
    );
  }
  if (mins < 24 * 60) {
    return (
      <Badge variant="outline"
        className="rounded-sm text-[10px] bg-amber-50 text-amber-800 border-amber-200">
        <SignalMedium className="h-3 w-3 mr-1" /> stale · {Math.round(mins / 60)}h ago
      </Badge>
    );
  }
  return (
    <Badge variant="outline"
      className="rounded-sm text-[10px] bg-red-50 text-red-700 border-red-200">
      <SignalLow className="h-3 w-3 mr-1" /> offline · {Math.round(mins / 60 / 24)}d ago
    </Badge>
  );
}

export default function PropertyDetailPage() {
  const { clientId, buId } = useParams();
  const [bu, setBu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [summary, setSummary] = useState(null);
  const [wpSites, setWpSites] = useState([]);
  const [wpLoading, setWpLoading] = useState(true);
  const [allClientSites, setAllClientSites] = useState([]);
  const [attaching, setAttaching] = useState(false);
  const [attachSel, setAttachSel] = useState("");
  const [domains, setDomains] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/business-units/${buId}`);
      setBu(data); setNotFound(false);
    } catch (e) {
      setBu(null); setNotFound(true);
    } finally { setLoading(false); }
  }, [buId]);

  const loadWp = useCallback(async () => {
    setWpLoading(true);
    try {
      const [{ data }, all] = await Promise.all([
        api.get(`/business-units/${buId}/wp-sites`),
        api.get(`/wp/sites`).catch(() => ({ data: [] })),
      ]);
      setWpSites(data?.sites || []);
      setAllClientSites((all.data || []).filter((s) => s.client_id === clientId));
    } catch (e) {
      setWpSites([]);
    } finally { setWpLoading(false); }
  }, [buId, clientId]);

  const loadDomains = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/domains`);
      const list = (data?.domains || []).filter((d) => d.business_unit_id === buId);
      setDomains(list);
    } catch { setDomains([]); }
  }, [clientId, buId]);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get(`/business-units/${buId}/budget-summary`);
      setSummary(data);
    } catch { setSummary(null); }
  }, [buId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadWp(); }, [loadWp]);
  useEffect(() => { loadDomains(); }, [loadDomains]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const unlinkedSites = useMemo(() => {
    const attachedIds = new Set(wpSites.map((s) => s.site_id));
    return allClientSites.filter((s) => !attachedIds.has(s.site_id)
      && (!s.business_unit_id || s.business_unit_id === buId));
  }, [allClientSites, wpSites, buId]);

  const attachSite = async () => {
    if (!attachSel) return;
    setAttaching(true);
    try {
      await api.patch(`/wp/sites/${attachSel}`, { business_unit_id: buId });
      toast.success("WordPress site attached to this property");
      setAttachSel("");
      await loadWp();
    } catch (e) {
      toast.error(errText(e, "Failed to attach site"));
    } finally { setAttaching(false); }
  };

  const detachSite = async (siteId) => {
    if (!window.confirm("Detach this WordPress site from the property?")) return;
    try {
      await api.patch(`/wp/sites/${siteId}`, { business_unit_id: "" });
      toast.success("Detached");
      await loadWp();
    } catch (e) {
      toast.error(errText(e, "Failed to detach"));
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="property-detail-loading">
        <Card className="rounded-sm"><CardContent className="p-6 flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading property…
        </CardContent></Card>
      </div>
    );
  }
  if (notFound || !bu) {
    return (
      <div className="space-y-4" data-testid="property-detail-not-found">
        <Link to={`/clients/${clientId}/properties`}
          className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-[#002FA7]">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to properties
        </Link>
        <Card className="rounded-sm">
          <CardContent className="p-8 text-center text-zinc-500 text-sm">
            Property not found or you do not have access.
          </CardContent>
        </Card>
      </div>
    );
  }

  const ogSrc = bu.og_image_url ? `${BACKEND}${bu.og_image_url}` : null;

  return (
    <div className="space-y-5" data-testid="property-detail-page">
      {/* Back nav */}
      <Link to={`/clients/${clientId}/properties`}
        className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-[#002FA7]"
        data-testid="property-detail-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to properties
      </Link>

      {/* Identity card */}
      <Card className="rounded-sm border-l-4 border-l-[#002FA7]">
        <CardContent className="p-5 flex items-start gap-4">
          <div className="w-32 h-20 shrink-0 overflow-hidden rounded-sm bg-zinc-900">
            {ogSrc ? (
              <img src={ogSrc} alt={bu.name} className="w-full h-full object-cover"
                data-testid="property-detail-og-image" />
            ) : (
              <div className="h-full w-full text-white flex items-center justify-center">
                <Globe2 className="h-6 w-6" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 space-y-1.5">
            <div className="flex items-center gap-2">
              <h1 className="font-display font-bold text-xl leading-tight"
                data-testid="property-detail-name">{bu.name}</h1>
              <Badge className="rounded-sm bg-[#002FA7]/10 text-[#002FA7] border-transparent text-[10px] font-bold uppercase">
                P{bu.priority ?? 5}
              </Badge>
            </div>
            <a href={bu.website_url} target="_blank" rel="noreferrer"
              className="text-xs text-zinc-500 inline-flex items-center gap-1 hover:text-[#002FA7]">
              {bu.website_url} <ExternalLink className="h-3 w-3" />
            </a>
            <div className="pt-1">
              <PlatformPills keys={bu.active_platforms || []} onEdit={() => {}} />
            </div>
            {bu.brief_summary && (
              <p className="text-xs text-zinc-600 pt-1 whitespace-pre-wrap leading-5 max-w-2xl">
                {bu.brief_summary}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Budget + traffic strip */}
      {summary && (
        <Card className="rounded-sm">
          <CardContent className="p-5">
            <div className="label-overline mb-3">Performance snapshot · {summary.period_month}</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-[11px] text-zinc-500 uppercase tracking-wider">Budget</div>
                <div className="font-display font-bold text-lg">${(summary.budget_total || 0).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[11px] text-zinc-500 uppercase tracking-wider">Actual spend</div>
                <div className="font-display font-bold text-lg">${(summary.actual_total || 0).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[11px] text-zinc-500 uppercase tracking-wider">Visits</div>
                <div className="font-display font-bold text-lg">{(summary.visits || 0).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[11px] text-zinc-500 uppercase tracking-wider">MoM traffic</div>
                <div className={`font-display font-bold text-lg ${
                  (summary.visits_pct_change || 0) > 0 ? "text-emerald-600"
                    : (summary.visits_pct_change || 0) < 0 ? "text-red-600" : ""}`}>
                  {summary.visits_pct_change != null ? `${summary.visits_pct_change > 0 ? "+" : ""}${summary.visits_pct_change.toFixed(1)}%` : "—"}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* WordPress sites card — the primary reason for this page */}
      <Card className="rounded-sm" data-testid="property-detail-wp-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="label-overline">WordPress sites</div>
              <div className="text-xs text-zinc-500">
                Sites attached to this property (explicit link + hostname match against{" "}
                <span className="font-mono">{bu.website_url}</span>).
              </div>
            </div>
            <Link to={`/wordpress?tab=sites`}
              className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1">
              WordPress API <ExternalLink className="h-3 w-3" />
            </Link>
          </div>

          {wpLoading ? (
            <div className="text-sm text-zinc-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading sites…
            </div>
          ) : wpSites.length === 0 ? (
            <div className="text-sm text-zinc-500 rounded-sm bg-zinc-50 border border-zinc-100 p-4"
              data-testid="property-detail-wp-empty">
              No WordPress sites attached to this property yet. Use the picker below
              to link one of this client&apos;s existing sites, or add a new site under
              the WordPress API page.
            </div>
          ) : (
            <div className="space-y-2">
              {wpSites.map((s) => (
                <div key={s.site_id}
                  className="flex items-center gap-3 rounded-sm border border-zinc-200 hover:border-[#002FA7]/40 p-3"
                  data-testid={`property-detail-wp-site-${s.site_id}`}>
                  <PanelsTopLeft className="h-5 w-5 text-[#002FA7] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Link to={`/wordpress/sites/${s.site_id}`}
                        className="font-semibold text-sm text-[#002FA7] hover:underline truncate"
                        data-testid={`property-detail-wp-open-${s.site_id}`}>
                        {s.name}
                      </Link>
                      {s.auto_matched && (
                        <Badge variant="outline"
                          className="rounded-sm text-[9px] bg-blue-50 text-blue-700 border-blue-200"
                          title="Matched by hostname — click 'Attach' below to make this link explicit.">
                          auto-matched
                        </Badge>
                      )}
                      {beaconFreshnessBadge(s)}
                      {s.detected?.wp_version && (
                        <Badge variant="outline"
                          className="rounded-sm text-[9px] bg-zinc-50 text-zinc-600 border-zinc-200"
                          title="Detected WordPress version">
                          WP {s.detected.wp_version}
                        </Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-500 font-mono truncate">
                      {s.url}
                    </div>
                    <div className="text-[11px] text-zinc-400 mt-0.5 flex items-center gap-3">
                      <span>Status: <span className="font-mono">{s.status || "unchecked"}</span></span>
                      <span>App-pw: {s.has_wp_auth ? "yes" : "no"}</span>
                      <span>Woo: {s.has_woo_keys ? "keys stored" : "no keys"}</span>
                      {s.detected?.plugin_updates_count > 0 && (
                        <span className="text-amber-700">
                          {s.detected.plugin_updates_count} plugin update{s.detected.plugin_updates_count === 1 ? "" : "s"} pending
                        </span>
                      )}
                    </div>
                  </div>
                  <Link to={`/wp-beacon`}
                    className="text-xs text-zinc-500 hover:text-[#002FA7] inline-flex items-center gap-1"
                    data-testid={`property-detail-wp-beacon-${s.site_id}`}
                    title="Open the master Beacon Dashboard">
                    Beacon
                  </Link>
                  {s.auto_matched ? (
                    <Button size="sm" variant="outline"
                      onClick={async () => {
                        try {
                          await api.patch(`/wp/sites/${s.site_id}`, { business_unit_id: buId });
                          toast.success("Attached explicitly");
                          await loadWp();
                        } catch (e) { toast.error(errText(e, "Failed")); }
                      }}
                      data-testid={`property-detail-wp-attach-${s.site_id}`}>
                      Attach
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700"
                      onClick={() => detachSite(s.site_id)}
                      data-testid={`property-detail-wp-detach-${s.site_id}`}
                      title="Detach this WP site from the property">
                      <Unlink className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Attach a new site picker */}
          {unlinkedSites.filter((s) => !wpSites.some((w) => w.site_id === s.site_id)).length > 0 && (
            <div className="flex items-center gap-2 pt-2 border-t border-zinc-100">
              <div className="text-xs text-zinc-500 shrink-0">Attach another site:</div>
              <Select value={attachSel} onValueChange={setAttachSel}>
                <SelectTrigger className="w-72" data-testid="property-detail-attach-select">
                  <SelectValue placeholder="Pick an unattached site…" />
                </SelectTrigger>
                <SelectContent>
                  {unlinkedSites
                    .filter((s) => !wpSites.some((w) => w.site_id === s.site_id))
                    .map((s) => (
                    <SelectItem key={s.site_id} value={s.site_id}>
                      {s.name} — {s.url.replace(/^https?:\/\//, "")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={attachSite}
                disabled={!attachSel || attaching}
                data-testid="property-detail-attach-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> {attaching ? "Attaching…" : "Attach"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Domains card */}
      {domains.length > 0 && (
        <Card className="rounded-sm" data-testid="property-detail-domains-card">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="label-overline">Domains ({domains.length})</div>
              <Link to={`/clients/${clientId}/domains`}
                className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1">
                All domains <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
            <div className="space-y-1.5">
              {domains.map((d) => (
                <Link key={d.domain_id}
                  to={`/clients/${clientId}/domains/${d.domain_id}`}
                  className="block rounded-sm border border-zinc-200 hover:border-[#002FA7]/40 p-2 text-sm hover:bg-zinc-50/60"
                  data-testid={`property-detail-domain-${d.domain_id}`}>
                  <span className="font-mono text-[13px] text-[#002FA7]">{d.name}</span>
                  <span className="text-[11px] text-zinc-500 ml-3">
                    {d.registrar || "—"} · expires {d.expiry || "—"}
                  </span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Keyphrases + competitors — always visible so it's clear what
          is / isn't being tracked. Edit link jumps back to the
          properties list which opens the inline SEO editor for this
          property. */}
      <Card className="rounded-sm" data-testid="property-detail-seo-card">
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-overline">
                Tracked keyphrases ({bu.keyphrases?.length || 0})
              </div>
              <Link
                to={`/clients/${clientId}/properties?editSeo=${buId}`}
                className="text-[11px] text-[#002FA7] hover:underline inline-flex items-center gap-1"
                data-testid="property-detail-edit-keyphrases">
                Edit <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
            {(bu.keyphrases?.length || 0) === 0 ? (
              <div className="text-[12px] text-zinc-500 rounded-sm bg-zinc-50 border border-zinc-100 p-3"
                data-testid="property-detail-keyphrases-empty">
                No keyphrases tracked yet. Add up to 100 phrases and we&apos;ll
                monitor Google &amp; Bing SERP ranks for this property.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5"
                data-testid="property-detail-keyphrases-list">
                {(bu.keyphrases || []).slice(0, 100).map((k, i) => (
                  <span key={i} className="text-[11px] px-1.5 py-0.5 bg-zinc-100 rounded-sm"
                    data-testid={`property-detail-keyphrase-${i}`}>
                    {k}
                  </span>
                ))}
                {(bu.keyphrases?.length || 0) > 100 && (
                  <span className="text-[11px] text-zinc-500">
                    +{bu.keyphrases.length - 100} more
                  </span>
                )}
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-overline">
                Competitors ({bu.competitors?.length || 0})
              </div>
              <Link
                to={`/clients/${clientId}/properties?editSeo=${buId}`}
                className="text-[11px] text-[#002FA7] hover:underline inline-flex items-center gap-1"
                data-testid="property-detail-edit-competitors">
                Edit <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
            {(bu.competitors?.length || 0) === 0 ? (
              <div className="text-[12px] text-zinc-500 rounded-sm bg-zinc-50 border border-zinc-100 p-3"
                data-testid="property-detail-competitors-empty">
                No competitors added yet. Add up to 10 competitor domains to
                include them in Share of Voice reports.
              </div>
            ) : (
              <div className="space-y-1.5"
                data-testid="property-detail-competitors-list">
                {(bu.competitors || []).map((c, i) => (
                  <div key={i}
                    className="text-[12px] text-zinc-700 flex items-center gap-2 flex-wrap"
                    data-testid={`property-detail-competitor-${i}`}>
                    <span className="font-semibold">{c.name || "(unnamed)"}</span>
                    {c.website_url && (
                      <a href={c.website_url} target="_blank" rel="noreferrer"
                        className="text-[11px] text-zinc-500 hover:text-[#002FA7] inline-flex items-center gap-1">
                        {c.website_url.replace(/^https?:\/\//, "")}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
